/**
 * Auth Middleware (Hono)
 *
 * Validates Bearer JWT or PAT (nxs_...) tokens, attaches `user`, `ability`
 * and related context to the Hono Context via `c.set()`.
 *
 * Extract token precedence:
 *  1. `Authorization: Bearer ...` header
 *  2. `?_token=...` query param (SSE / EventSource can't set custom headers)
 */

import jwt from 'jsonwebtoken'
import type { MiddlewareHandler, Context } from 'hono'
import { createMiddleware } from 'hono/factory'
import type { ModuleContext, ManagedCache } from '@gzl10/nexus-sdk'
import { SYSTEM_CLAIMS, type JwtPayload } from './jwt.utils.js'
import type { AuthUserRecord } from './auth.types.js'
import type { AuthService } from './auth.service.js'
import type { UsersService } from '../users/users.types.js'
import { defineAnonymousAbility } from '@kernel/abilities/ability.factory.js'
import type { AppAbility } from '@kernel/abilities/ability.types.js'

// Table name (hardcoded for module isolation)
const USERS = 'users'

/** Extract JWT / PAT token from the Hono request. */
function extractToken(c: Context): string | null {
  const authHeader = c.req.header('authorization')
  if (authHeader?.startsWith('Bearer ')) {
    return authHeader.slice(7)
  }
  const queryToken = c.req.query('_token')
  if (typeof queryToken === 'string' && queryToken.length > 0) {
    return queryToken
  }
  return null
}

/** Build read-only ability by filtering rules to read-only actions */
async function buildReadOnlyAbility(
  defineAbilityFor: ModuleContext['core']['abilities']['defineAbilityFor'],
  user: AuthUserRecord,
  roleNames: string[]
): Promise<AppAbility> {
  const fullAbility = await defineAbilityFor(user, roleNames) as unknown as AppAbility

  const readOnlyRules = (fullAbility as unknown as { rules: Array<Record<string, unknown>> }).rules
    .filter(rule => !rule['inverted'])
    .map((rule) => {
      const action = rule['action']
      if (action === 'manage' || (Array.isArray(action) && action.includes('manage'))) {
        return { ...rule, action: 'read' }
      }
      if (action === 'read' || (Array.isArray(action) && action.includes('read'))) {
        return rule
      }
      return null
    })
    .filter(Boolean) as Array<Record<string, unknown>>

  const { createMongoAbility } = await import('@casl/ability')
  return createMongoAbility(readOnlyRules as never) as unknown as AppAbility
}

/** Get ability from cache or build and cache it */
async function getCachedAbility(
  abilityCache: ManagedCache<AppAbility>,
  defineAbilityFor: ModuleContext['core']['abilities']['defineAbilityFor'],
  user: AuthUserRecord,
  roleNames: string[]
): Promise<AppAbility> {
  const cacheKey = `${user.id}:${roleNames.sort().join(',')}`
  const cached = await abilityCache.get(cacheKey)
  if (cached) return cached

  const ability = await defineAbilityFor(user, roleNames) as unknown as AppAbility
  await abilityCache.set(cacheKey, ability)
  return ability
}

/** Extract non-system JWT claims into a plain object. */
function extractCustomClaims(decoded: JwtPayload): Record<string, unknown> | undefined {
  const decodedRaw = decoded as unknown as Record<string, unknown>
  const customClaims: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(decodedRaw)) {
    if (!SYSTEM_CLAIMS.has(key)) customClaims[key] = value
  }
  return Object.keys(customClaims).length > 0 ? customClaims : undefined
}

/**
 * Creates both auth middlewares (required + optional) sharing a single ability cache.
 */
export function createAuthMiddlewares(
  ctx: ModuleContext,
  secret: string
): { auth: MiddlewareHandler; optionalAuth: MiddlewareHandler } {
  const { errors, abilities } = ctx.core
  const { defineAbilityFor } = abilities
  const usersService = ctx.services.get<UsersService>('users')

  const abilityCache = ctx.core.cache.create<AppAbility>('auth:ability', {
    maxEntries: 500,
    defaultTTL: 60,
    invalidateOn: ['db.roles.*', 'db.permissions.*', 'db.user_roles.*']
  })

  const auth = createMiddleware(async (c, next) => {
    const token = extractToken(c)

    if (!token) {
      throw new errors.UnauthorizedError('Token no proporcionado')
    }

    // PAT path
    if (token.startsWith('nxs_')) {
      const authService = ctx.services.get<AuthService>('auth')
      const result = await authService.validatePersonalToken(token)

      if (!result) {
        throw new errors.UnauthorizedError('Token inválido o expirado')
      }

      const user = await ctx.db.knex<AuthUserRecord>(USERS).where({ id: result.userId }).first()
      if (!user) {
        throw new errors.UnauthorizedError('Usuario no encontrado')
      }

      const roleNames = await usersService.getRoleNames(user.id)
      const ability = result.scope === 'readonly'
        ? await buildReadOnlyAbility(defineAbilityFor, user, roleNames)
        : await getCachedAbility(abilityCache, defineAbilityFor, user, roleNames)

      ;(c as Context).set('user', user)
      ;(c as Context).set('ability', ability)
      ;(c as Context).set('patTokenId', result.tokenId)
      await next()
      return
    }

    // JWT path
    try {
      const decoded = jwt.verify(token, secret) as unknown as JwtPayload

      const user = await ctx.db.knex<AuthUserRecord>(USERS).where({ id: decoded.userId }).first()

      if (!user) {
        throw new errors.UnauthorizedError('Usuario no encontrado')
      }

      const roleNames = await usersService.getRoleNames(user.id)
      const ability = await getCachedAbility(abilityCache, defineAbilityFor, user, roleNames)

      ;(c as Context).set('user', user)
      ;(c as Context).set('ability', ability)

      if (decoded.impersonatedBy) {
        ;(c as Context).set('impersonation', {
          originalUserId: decoded.impersonatedBy,
          isImpersonating: true
        })
      }

      const claims = extractCustomClaims(decoded)
      if (claims) {
        ;(c as Context).set('claims', claims)
      }

      await next()
    } catch (error) {
      if (error instanceof jwt.TokenExpiredError) {
        throw new errors.UnauthorizedError('Token expirado')
      }
      if (error instanceof jwt.JsonWebTokenError) {
        throw new errors.UnauthorizedError('Token inválido')
      }
      throw error
    }
  })

  const optionalAuth = createMiddleware(async (c, next) => {
    const token = extractToken(c)

    if (!token) {
      ;(c as Context).set('ability', defineAnonymousAbility() as unknown as AppAbility)
      await next()
      return
    }

    // PAT path
    if (token.startsWith('nxs_')) {
      try {
        const authService = ctx.services.get<AuthService>('auth')
        const result = await authService.validatePersonalToken(token)

        if (result) {
          const user = await ctx.db.knex<AuthUserRecord>(USERS).where({ id: result.userId }).first()
          if (user) {
            const roleNames = await usersService.getRoleNames(user.id)
            const ability = result.scope === 'readonly'
              ? await buildReadOnlyAbility(defineAbilityFor, user, roleNames)
              : await getCachedAbility(abilityCache, defineAbilityFor, user, roleNames)

            ;(c as Context).set('user', user)
            ;(c as Context).set('ability', ability)
            ;(c as Context).set('patTokenId', result.tokenId)
          }
        }
      } catch {
        // Token inválido, continuar sin autenticación
      }
      await next()
      return
    }

    try {
      const decoded = jwt.verify(token, secret) as unknown as JwtPayload

      const user = await ctx.db.knex<AuthUserRecord>(USERS).where({ id: decoded.userId }).first()

      if (user) {
        const roleNames = await usersService.getRoleNames(user.id)
        const ability = await getCachedAbility(abilityCache, defineAbilityFor, user, roleNames)

        ;(c as Context).set('user', user)
        ;(c as Context).set('ability', ability)

        if (decoded.impersonatedBy) {
          ;(c as Context).set('impersonation', {
            originalUserId: decoded.impersonatedBy,
            isImpersonating: true
          })
        }

        const claims = extractCustomClaims(decoded)
        if (claims) {
          ;(c as Context).set('claims', claims)
        }
      }
    } catch {
      // Token inválido, continuar sin autenticación
    }

    await next()
  })

  return { auth, optionalAuth }
}
